import { pdfOptions } from 'ComponentLibrary/src/common/pdf'
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Box, InputBase, Stack, TextField } from '@mui/material'
import { tokens } from 'ComponentLibrary'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { CaretDownIcon, CaretLeftIcon, CaretRightIcon, CaretUpIcon, MagnifyingGlassIcon, MinusIcon, PlusIcon } from '@phosphor-icons/react'
import { Document, Page } from 'react-pdf'
import type { PDFDocumentProxy, PDFPageProxy, TextItem } from 'pdfjs-dist/types/src/display/api.js'

import { useFocusHighlights } from './use-focus-highlights'
import {
    buildGlyphs,
    buildSearchVariants,
    matchVariantRectangles,
    normalizeTextForMatching,
    type NormalizedHighlight,
} from './highlight-engine'
import {
    DocumentWrapper,
    FocusHighlightOverlay,
    FocusHighlightRect,
    HeaderBar,
    NavControls,
    PdfViewerContainer,
    SearchHighlightOverlay,
    SearchHighlightRect,
    SearchInfo,
} from './styled'
import { PDF_STAGE_HEIGHT_PX, PDF_STAGE_WIDTH_PX } from './constants'

export type PdfPreviewSectionCopy = {
    searchLabel: string
    searchPlaceholder: string
    previousResult: string
    nextResult: string
    noResults: string
    searchResultMessage: (current: number, total: number, page: number) => string
    pdfLoadError: string
    previousPage: string
    nextPage: string
    page: string
    zoomOut: string
    zoomIn: string
}

export const DEFAULT_PDF_PREVIEW_COPY: PdfPreviewSectionCopy = {
    searchLabel: 'Suche',
    searchPlaceholder: 'PDF durchsuchen',
    previousResult: 'Vorheriger Treffer',
    nextResult: 'Nächster Treffer',
    noResults: 'Keine Treffer gefunden.',
    searchResultMessage: (current, total, page) => `Treffer ${current} von ${total} (Seite ${page})`,
    pdfLoadError: 'PDF konnte nicht angezeigt werden.',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    page: 'Seite',
    zoomOut: 'Verkleinern',
    zoomIn: 'Vergrößern',
}

type PdfPreviewSectionProps = {
    pdfUrl: string | null
    pageTexts: string[]
    focusHighlight: { term: string; page?: number | null; align?: 'exact' | 'offset' } | null
    onFocusHighlightChange: (value: { term: string; page?: number | null; align?: 'exact' | 'offset' } | null) => void
    floatingSearchControls?: boolean
    pageJumpTarget?: { page: number; key: number; align?: 'exact' | 'offset' } | null
    showPageShadow?: boolean
    onInitialLayoutReady?: () => void
    initialDocumentMinHeightPx?: number
    copy?: PdfPreviewSectionCopy
}

type SearchMatch = {
    pageNumber: number
}

const ZOOM_MIN = 0.5
const ZOOM_MAX = 2
const ZOOM_STEP = 0.25
const PROGRAMMATIC_SCROLL_TOP_OFFSET_RATIO = 0.08
const PROGRAMMATIC_SCROLL_TOP_OFFSET_MAX_PX = 160
const FOCUS_HIGHLIGHT_SCROLL_OFFSET_PX = 50
const PAGE_FRAME_BORDER_WIDTH_PX = Number.parseInt(tokens.SizingBorderS, 10) || 0

export const PdfPreviewSection = ({
    pdfUrl,
    pageTexts,
    focusHighlight,
    onFocusHighlightChange,
    floatingSearchControls = false,
    pageJumpTarget = null,
    showPageShadow = false,
    onInitialLayoutReady,
    initialDocumentMinHeightPx,
    copy = DEFAULT_PDF_PREVIEW_COPY,
}: PdfPreviewSectionProps) => {
    const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null)
    const [searchHighlights, setSearchHighlights] = useState<Record<number, NormalizedHighlight[]>>({})
    const [pageHeight, setPageHeight] = useState<number | null>(null)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [normalizedSearchTerm, setNormalizedSearchTerm] = useState<string>('')
    const [searchMatches, setSearchMatches] = useState<SearchMatch[]>([])
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(-1)
    const [searchMessage, setSearchMessage] = useState<string | null>(null)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [jumpPageInput, setJumpPageInput] = useState<string>('1')
    const [loadedDocumentPages, setLoadedDocumentPages] = useState<number>(0)
    const viewerResizeObserver = useRef<{ disconnect: () => void } | null>(null)
    const pdfViewerRef = useRef<HTMLDivElement | null>(null)
    const pageRefs = useRef<(HTMLDivElement | null)[]>([])
    const [zoom, setZoom] = useState<number>(1)
    const [viewerWidth, setViewerWidth] = useState<number | null>(null)
    const viewerWidthObserverRef = useRef<ResizeObserver | null>(null)
    const zoomScrollSnapshotRef = useRef<{
        scrollTop: number
        scrollHeight: number
        clientHeight: number
    } | null>(null)
    // Track programmatic scroll target to suppress scroll-driven page updates
    const programmaticScrollTargetRef = useRef<number | null>(null)
    const programmaticScrollCancelTimerRef = useRef<number | null>(null)
    const hasNotifiedInitialLayoutReadyRef = useRef(false)

    const attachViewerRef = useCallback((element: HTMLDivElement | null) => {
        pdfViewerRef.current = element
    }, [])

    // Clear any pending suppression timer on unmount
    useEffect(() => {
        return () => {
            if (programmaticScrollCancelTimerRef.current !== null) {
                window.clearTimeout(programmaticScrollCancelTimerRef.current)
                programmaticScrollCancelTimerRef.current = null
            }
            programmaticScrollTargetRef.current = null
            if (viewerWidthObserverRef.current) {
                viewerWidthObserverRef.current.disconnect()
                viewerWidthObserverRef.current = null
            }
            zoomScrollSnapshotRef.current = null
        }
    }, [])

    useEffect(() => {
        pageRefs.current = []
        setSearchTerm('')
        setNormalizedSearchTerm('')
        setSearchMatches([])
        setCurrentMatchIndex(-1)
        setSearchMessage(null)
        setCurrentPage(1)
        setJumpPageInput('1')
        setLoadedDocumentPages(pageTexts.length > 0 ? pageTexts.length : 0)
        setPageHeight(null)
        zoomScrollSnapshotRef.current = null
        if (pdfViewerRef.current) {
            pdfViewerRef.current.scrollTo({ top: 0, behavior: 'auto' })
        }
        hasNotifiedInitialLayoutReadyRef.current = false
    }, [pageTexts.length, pdfUrl])

    useEffect(() => {
        const container = pdfViewerRef.current
        if (!container) {
            setViewerWidth(null)
            return
        }

        const updateWidth = () => {
            const nextWidth = Math.max(container.clientWidth, 0)
            setViewerWidth((prev) => (prev !== nextWidth ? nextWidth : prev))
        }

        updateWidth()

        if (viewerWidthObserverRef.current) {
            viewerWidthObserverRef.current.disconnect()
            viewerWidthObserverRef.current = null
        }

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(() => {
                updateWidth()
            })
            observer.observe(container)
            viewerWidthObserverRef.current = observer
            return () => {
                observer.disconnect()
                if (viewerWidthObserverRef.current === observer) {
                    viewerWidthObserverRef.current = null
                }
            }
        }

        window.addEventListener('resize', updateWidth)
        return () => {
            window.removeEventListener('resize', updateWidth)
        }
    }, [pdfUrl])

    const maxAvailablePages = useMemo(() => {
        const candidates = [loadedDocumentPages, pageTexts.length].filter((value): value is number => Number.isFinite(value) && value > 0)
        return candidates.length > 0 ? Math.max(...candidates) : 1
    }, [loadedDocumentPages, pageTexts.length])

    const normalizedCurrentSearch = searchTerm.trim().toLowerCase()
    const hasTriggeredSearch = normalizedSearchTerm.length > 0
    const hasActiveSearch = hasTriggeredSearch && normalizedSearchTerm === normalizedCurrentSearch
    const hasMatches = hasActiveSearch && searchMatches.length > 0
    const highlightTerm = hasActiveSearch ? searchTerm.trim() : ''
    const trimmedSearchTerm = useMemo(() => highlightTerm.trim(), [highlightTerm])

    const resetSearchHighlights = useCallback(() => {
        setSearchHighlights({})
    }, [])

    const scrollToPage = useCallback((pageNumber: number, behavior: ScrollBehavior = 'smooth', align: 'exact' | 'offset' = 'offset') => {
        const container = pdfViewerRef.current
        if (!container) {
            return
        }
        const getTargetScrollTop = (targetElement: HTMLDivElement): number => {
            if (align === 'exact') {
                return Math.max(targetElement.offsetTop, 0)
            }
            const topOffset = Math.min(
                targetElement.offsetHeight * PROGRAMMATIC_SCROLL_TOP_OFFSET_RATIO,
                PROGRAMMATIC_SCROLL_TOP_OFFSET_MAX_PX
            )
            return Math.max(targetElement.offsetTop - topOffset, 0)
        }
        const target = pageRefs.current[pageNumber - 1]
        if (target) {
            container.scrollTo({
                top: getTargetScrollTop(target),
                behavior,
            })
            return
        }
        requestAnimationFrame(() => {
            const nextTarget = pageRefs.current[pageNumber - 1]
            if (nextTarget) {
                container.scrollTo({
                    top: getTargetScrollTop(nextTarget),
                    behavior,
                })
            }
        })
    }, [])

    const updateSearchMessage = useCallback(
        (term: string, matches: SearchMatch[], index: number) => {
            if (!term || matches.length === 0) {
                setSearchMessage(copy.noResults)
                return
            }
            const match = matches[index]
            setSearchMessage(copy.searchResultMessage(index + 1, matches.length, match.pageNumber))
        },
        [copy]
    )

    const findMatches = useCallback(
        (term: string): SearchMatch[] => {
            if (!term || pageTexts.length === 0) {
                return []
            }
            const lowerTerm = term.toLowerCase()
            const matches: SearchMatch[] = []
            pageTexts.forEach((text: string, index: number) => {
                if (text.toLowerCase().includes(lowerTerm)) {
                    matches.push({ pageNumber: index + 1 })
                }
            })
            return matches
        },
        [pageTexts]
    )

    const goToPage = useCallback(
        (pageNumber: number, behavior: ScrollBehavior = 'smooth', align: 'exact' | 'offset' = 'offset') => {
            const clamped = Math.min(Math.max(pageNumber, 1), maxAvailablePages)
            // Mark that we're performing a programmatic scroll to a specific page
            programmaticScrollTargetRef.current = clamped
            // Clear any previous cancel timer
            if (programmaticScrollCancelTimerRef.current !== null) {
                window.clearTimeout(programmaticScrollCancelTimerRef.current)
                programmaticScrollCancelTimerRef.current = null
            }
            // Failsafe: if we don't hit the target within a short time, re-enable scroll updates
            programmaticScrollCancelTimerRef.current = window.setTimeout(() => {
                programmaticScrollTargetRef.current = null
                programmaticScrollCancelTimerRef.current = null
            }, 1200)

            setCurrentPage(clamped)
            scrollToPage(clamped, behavior, align)
        },
        [maxAvailablePages, scrollToPage]
    )

    const performSearch = useCallback(
        (mode: 'initial' | 'next' | 'previous') => {
            const trimmed = searchTerm.trim()
            if (!trimmed) {
                setNormalizedSearchTerm('')
                setSearchMatches([])
                setCurrentMatchIndex(-1)
                setSearchMessage(null)
                onFocusHighlightChange(null)
                return
            }

            const normalized = trimmed.toLowerCase()
            onFocusHighlightChange(null)
            let matches = searchMatches
            if (normalized !== normalizedSearchTerm || matches.length === 0) {
                matches = findMatches(normalized)
                setNormalizedSearchTerm(normalized)
                setSearchMatches(matches)
                if (matches.length === 0) {
                    setCurrentMatchIndex(-1)
                    setSearchMessage(copy.noResults)
                    return
                }
                const startIndex = mode === 'previous' ? matches.length - 1 : 0
                setCurrentMatchIndex(startIndex)
                updateSearchMessage(trimmed, matches, startIndex)
                goToPage(matches[startIndex].pageNumber)
                return
            }

            if (matches.length === 0) {
                setSearchMessage(copy.noResults)
                return
            }

            let nextIndex
            if (mode === 'previous') {
                nextIndex = (currentMatchIndex - 1 + matches.length) % matches.length
            } else {
                nextIndex = (currentMatchIndex + 1) % matches.length
            }
            setCurrentMatchIndex(nextIndex)
            updateSearchMessage(trimmed, matches, nextIndex)
            goToPage(matches[nextIndex].pageNumber)
        },
        [
            currentMatchIndex,
            findMatches,
            goToPage,
            copy.noResults,
            normalizedSearchTerm,
            onFocusHighlightChange,
            searchMatches,
            searchTerm,
            updateSearchMessage,
        ]
    )

    const performSearchRef = useRef(performSearch)
    useEffect(() => {
        performSearchRef.current = performSearch
    }, [performSearch])

    const collectHighlightsForPage = useCallback(
        async (pageNumber: number, variants: string[]): Promise<NormalizedHighlight[]> => {
            if (!pdfDocument) {
                return []
            }

            const page = await pdfDocument.getPage(pageNumber)
            const viewport = page.getViewport({ scale: 1 })
            const textContent = await page.getTextContent({ disableCombineTextItems: true } as Parameters<
                PDFPageProxy['getTextContent']
            >[0])
            const glyphs = buildGlyphs(textContent.items as TextItem[], viewport)
            return matchVariantRectangles(glyphs, variants)
        },
        [pdfDocument]
    )

    const collectNormalizedPageText = useCallback(
        async (pageNumber: number): Promise<string> => {
            if (!pdfDocument) {
                return ''
            }

            const page = await pdfDocument.getPage(pageNumber)
            const textContent = await page.getTextContent({ disableCombineTextItems: true } as Parameters<
                PDFPageProxy['getTextContent']
            >[0])
            const pageText = (textContent.items as TextItem[]).map((item) => item.str ?? '').join('')
            return normalizeTextForMatching(pageText)
        },
        [pdfDocument]
    )

    const { focusHighlights, focusPage, resetFocusHighlights, trimmedFocusTerm } = useFocusHighlights({
        collectHighlightsForPage,
        collectNormalizedPageText,
        focusHighlight,
        onFocusHighlightChange,
        pdfDocument,
    })
    const focusAlign = focusHighlight?.align ?? 'offset'

    const handleSearchSubmit = useCallback(() => {
        performSearch('initial')
    }, [performSearch])

    const goToNextMatch = useCallback(() => {
        performSearch('next')
    }, [performSearch])

    const goToPreviousMatch = useCallback(() => {
        performSearch('previous')
    }, [performSearch])

    const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            handleSearchSubmit()
            return
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            goToNextMatch()
            return
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault()
            goToPreviousMatch()
        }
    }

    useEffect(() => {
        const handle = window.setTimeout(() => {
            performSearchRef.current?.('initial')
        }, 500)

        return () => {
            window.clearTimeout(handle)
        }
    }, [searchTerm])

    const handleViewerScroll = useCallback(() => {
        const container = pdfViewerRef.current
        if (!container || pageRefs.current.length === 0) {
            return
        }
        const scrollTop = container.scrollTop
        const nextIndex = pageRefs.current.findIndex((page) => {
            if (!page) {
                return false
            }
            const offsetTop = page.offsetTop
            const offsetBottom = offsetTop + page.offsetHeight
            const anchor = scrollTop + 16
            return anchor >= offsetTop && anchor < offsetBottom
        })

        // If a programmatic scroll is underway, suppress scroll-driven page updates
        const target = programmaticScrollTargetRef.current
        if (target !== null) {
            if (nextIndex !== -1 && nextIndex + 1 === target) {
                // We reached the desired page; clear suppression
                programmaticScrollTargetRef.current = null
                if (programmaticScrollCancelTimerRef.current !== null) {
                    window.clearTimeout(programmaticScrollCancelTimerRef.current)
                    programmaticScrollCancelTimerRef.current = null
                }
                // currentPage already set to target in goToPage, so no need to update
            }
            return
        }

        if (nextIndex !== -1 && currentPage !== nextIndex + 1) {
            setCurrentPage(nextIndex + 1)
        }
    }, [currentPage])

    const scrollToFocusHighlight = useCallback(
        (pageNumber: number, behavior: ScrollBehavior = 'smooth') => {
            const container = pdfViewerRef.current
            const target = pageRefs.current[pageNumber - 1]
            const pageHighlights = focusHighlights[pageNumber] ?? []

            if (!container || !target || pageHighlights.length === 0) {
                return
            }

            const topmostHighlight = pageHighlights.reduce((currentTop, highlight) => (highlight.y < currentTop.y ? highlight : currentTop))
            const targetScrollTop = Math.max(
                target.offsetTop + target.offsetHeight * topmostHighlight.y - FOCUS_HIGHLIGHT_SCROLL_OFFSET_PX,
                0
            )

            container.scrollTo({
                top: targetScrollTop,
                behavior,
            })
        },
        [focusHighlights]
    )

    useEffect(() => {
        let isCancelled = false

        const computeHighlights = async () => {
            if (!pdfDocument || !trimmedSearchTerm) {
                resetSearchHighlights()
                return
            }

            const variants = buildSearchVariants(trimmedSearchTerm)
            const nextHighlights: Record<number, NormalizedHighlight[]> = {}

            for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
                if (isCancelled) {
                    return
                }

                try {
                    const pageRects = await collectHighlightsForPage(pageNumber, variants)
                    if (pageRects.length > 0) {
                        nextHighlights[pageNumber] = pageRects
                    }
                } catch {
                    // Swallow highlight computation errors; proceed with remaining pages.
                }
            }

            if (!isCancelled) {
                setSearchHighlights(nextHighlights)
            }
        }

        void computeHighlights()

        return () => {
            isCancelled = true
        }
    }, [collectHighlightsForPage, pdfDocument, resetSearchHighlights, trimmedSearchTerm])

    useEffect(() => {
        if (!trimmedSearchTerm) {
            resetSearchHighlights()
        }
    }, [resetSearchHighlights, trimmedSearchTerm])

    useEffect(() => {
        if (!pdfDocument) {
            resetSearchHighlights()
        }
    }, [pdfDocument, resetSearchHighlights])

    // When focusing by term without explicit page, auto-scroll to the first page that has a focus highlight
    useEffect(() => {
        if (!trimmedFocusTerm || focusPage) {
            return
        }
        const pages = Object.keys(focusHighlights)
            .map((k) => Number(k))
            .filter((n) => Number.isFinite(n) && n >= 1)
        if (pages.length > 0) {
            const firstPageWithFocus = Math.min(...pages)
            goToPage(firstPageWithFocus)
        }
    }, [focusHighlights, focusPage, goToPage, trimmedFocusTerm])

    useEffect(() => {
        if (!pdfUrl) {
            setPdfDocument(null)
            resetSearchHighlights()
            resetFocusHighlights()
            setPageHeight(null)
        }
    }, [pdfUrl, resetFocusHighlights, resetSearchHighlights])

    useEffect(() => {
        if (viewerResizeObserver.current) {
            viewerResizeObserver.current.disconnect()
            viewerResizeObserver.current = null
        }

        let animationFrame: number | null = null
        let isCancelled = false

        const attachObserver = () => {
            if (isCancelled) {
                return
            }

            if (!pdfUrl || loadedDocumentPages <= 0) {
                setPageHeight(null)
                return
            }

            const firstPageElement = pageRefs.current[0]
            if (!firstPageElement) {
                animationFrame = window.requestAnimationFrame(attachObserver)
                return
            }

            const updateHeight = () => {
                const rect = firstPageElement.getBoundingClientRect()
                const nextHeight = Math.ceil(rect.height)
                if (Number.isFinite(nextHeight) && nextHeight > 0) {
                    setPageHeight((prev) => (prev !== nextHeight ? nextHeight : prev))
                }
            }

            updateHeight()

            if (typeof ResizeObserver !== 'undefined') {
                const observer = new ResizeObserver(() => {
                    updateHeight()
                })
                observer.observe(firstPageElement)
                viewerResizeObserver.current = { disconnect: () => observer.disconnect() }
            } else {
                const handleResize = () => updateHeight()
                window.addEventListener('resize', handleResize)
                viewerResizeObserver.current = {
                    disconnect: () => {
                        window.removeEventListener('resize', handleResize)
                    },
                }
            }
        }

        attachObserver()

        return () => {
            isCancelled = true
            if (animationFrame !== null) {
                window.cancelAnimationFrame(animationFrame)
            }
            if (viewerResizeObserver.current) {
                viewerResizeObserver.current.disconnect()
                viewerResizeObserver.current = null
            }
        }
    }, [loadedDocumentPages, pdfUrl])

    useEffect(() => {
        if (focusPage) {
            goToPage(focusPage, 'smooth', focusAlign)
        }
    }, [focusAlign, focusPage, goToPage])

    useEffect(() => {
        if (!pdfUrl || loadedDocumentPages <= 0 || pageHeight === null || hasNotifiedInitialLayoutReadyRef.current) {
            return
        }

        hasNotifiedInitialLayoutReadyRef.current = true
        onInitialLayoutReady?.()
    }, [loadedDocumentPages, onInitialLayoutReady, pageHeight, pdfUrl])

    useEffect(() => {
        if (!focusPage || focusAlign !== 'exact' || (focusHighlights[focusPage]?.length ?? 0) === 0) {
            return
        }

        scrollToFocusHighlight(focusPage, 'smooth')
    }, [focusAlign, focusHighlights, focusPage, scrollToFocusHighlight])

    useEffect(() => {
        if (pageJumpTarget?.page) {
            goToPage(pageJumpTarget.page, 'smooth', pageJumpTarget.align ?? 'offset')
        }
    }, [goToPage, pageJumpTarget])

    const handleDocumentLoadSuccessInternal = useCallback((documentProxy: PDFDocumentProxy) => {
        setPdfDocument(documentProxy)
        setLoadedDocumentPages(documentProxy.numPages)
        pageRefs.current = Array(documentProxy.numPages).fill(null)
        setCurrentPage((prev) => Math.min(prev, documentProxy.numPages))
        pdfViewerRef.current?.scrollTo({ top: 0, behavior: 'auto' })
    }, [])

    const clampZoom = useCallback((value: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, value)), [])

    const captureZoomScrollSnapshot = useCallback(() => {
        const container = pdfViewerRef.current
        if (!container) {
            zoomScrollSnapshotRef.current = null
            return
        }
        zoomScrollSnapshotRef.current = {
            scrollTop: container.scrollTop,
            scrollHeight: container.scrollHeight,
            clientHeight: container.clientHeight,
        }
    }, [])

    const applyZoom = useCallback(
        (nextZoomValue: number) => {
            const clamped = clampZoom(Number(nextZoomValue.toFixed(2)))
            if (Math.abs(clamped - zoom) < 0.001) {
                return
            }
            captureZoomScrollSnapshot()
            setZoom(clamped)
        },
        [captureZoomScrollSnapshot, clampZoom, zoom]
    )

    const handleZoomStep = useCallback(
        (direction: 'in' | 'out') => {
            const delta = direction === 'in' ? ZOOM_STEP : -ZOOM_STEP
            applyZoom(zoom + delta)
        },
        [applyZoom, zoom]
    )

    const effectiveViewerWidth = viewerWidth ? Math.min(viewerWidth, PDF_STAGE_WIDTH_PX) : null
    const pageWidthPx = effectiveViewerWidth ? Math.max(1, Math.round(effectiveViewerWidth * zoom)) : null
    const renderedPageWidthPx =
        pageWidthPx === null ? null : Math.max(1, pageWidthPx - (showPageShadow ? PAGE_FRAME_BORDER_WIDTH_PX * 2 : 0))

    useEffect(() => {
        setJumpPageInput((prev) => (prev === String(currentPage) ? prev : String(currentPage)))
    }, [currentPage])

    useLayoutEffect(() => {
        const snapshot = zoomScrollSnapshotRef.current
        if (!snapshot) {
            return
        }
        const container = pdfViewerRef.current
        if (!container) {
            zoomScrollSnapshotRef.current = null
            return
        }

        const restore = () => {
            const currentSnapshot = zoomScrollSnapshotRef.current
            if (!currentSnapshot) {
                return
            }
            const { scrollTop, scrollHeight, clientHeight } = currentSnapshot
            const previousScrollable = Math.max(scrollHeight - clientHeight, 1)
            const ratio = previousScrollable > 0 ? scrollTop / previousScrollable : 0
            const targetScrollable = Math.max(container.scrollHeight - container.clientHeight, 0)
            const targetTop = targetScrollable > 0 ? ratio * targetScrollable : 0
            container.scrollTo({ top: targetTop })
            zoomScrollSnapshotRef.current = null
        }

        const frame = window.requestAnimationFrame(() => {
            window.requestAnimationFrame(restore)
        })

        return () => {
            window.cancelAnimationFrame(frame)
        }
    }, [pageWidthPx])

    const searchControls = (
        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="flex-end" justifyContent="space-between" width="100%">
            <Stack direction="column" gap={tokens.SizingSpacing050} flex={1} minWidth="240px">
                <TextField
                    label={copy.searchLabel}
                    size="small"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    onKeyDown={handleSearchKeyDown}
                />
            </Stack>
            <NavControls direction="row" gap={tokens.SizingSpacing050} alignItems="flex-end">
                <IconButton
                    size="small"
                    variant="secondary"
                    icon={<CaretUpIcon />}
                    ariaLabel={copy.previousResult}
                    tooltip={copy.previousResult}
                    onClick={goToPreviousMatch}
                    disabled={!hasMatches}
                />
                <IconButton
                    size="small"
                    variant="secondary"
                    icon={<CaretDownIcon />}
                    ariaLabel={copy.nextResult}
                    tooltip={copy.nextResult}
                    onClick={goToNextMatch}
                    disabled={!hasMatches}
                />
            </NavControls>
        </Stack>
    )

    const floatingSearchControl = (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 7,
                padding: `${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`,
                pointerEvents: 'none',
            }}
        >
            <Box
                sx={{
                    width: 'clamp(200px, 33.333%, 360px)',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingX: tokens.SizingSpacing150,
                    borderRadius: tokens.SizingCornerS,
                    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                    backgroundColor: tokens.ColorSurfaceDefault,
                    boxShadow: tokens.Popup.boxShadow,
                    pointerEvents: 'auto',
                }}
            >
                <InputBase
                    value={searchTerm}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder={copy.searchPlaceholder}
                    inputProps={{
                        'aria-label': copy.searchPlaceholder,
                    }}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiInputBase-input': {
                            ...tokens.TextM,
                            color: tokens.ColorTextBold,
                            padding: 0,
                            '::placeholder': {
                                color: tokens.ColorTextSubtle,
                                opacity: 1,
                            },
                        },
                    }}
                />
                <Stack direction="row" alignItems="center" gap={0} paddingLeft={tokens.SizingSpacing050} marginLeft="auto">
                    {searchMatches.length > 1 ? (
                        <Stack direction="column" gap={0} justifyContent="center" alignItems="center" sx={{ flexShrink: 0 }}>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                    if (hasMatches) {
                                        goToPreviousMatch()
                                    }
                                }}
                                disabled={!hasMatches}
                                aria-label={copy.previousResult}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '16px',
                                    height: '12px',
                                    padding: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    color: hasMatches ? tokens.ColorIconBoldest : tokens.ColorIconDisabled,
                                    cursor: hasMatches ? 'pointer' : 'default',
                                    flexShrink: 0,
                                }}
                            >
                                <CaretUpIcon size={12} weight="bold" />
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                onClick={() => {
                                    if (hasMatches) {
                                        goToNextMatch()
                                    }
                                }}
                                disabled={!hasMatches}
                                aria-label={copy.nextResult}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '16px',
                                    height: '12px',
                                    padding: 0,
                                    border: 'none',
                                    background: 'transparent',
                                    color: hasMatches ? tokens.ColorIconBoldest : tokens.ColorIconDisabled,
                                    cursor: hasMatches ? 'pointer' : 'default',
                                    flexShrink: 0,
                                }}
                            >
                                <CaretDownIcon size={12} weight="bold" />
                            </Box>
                        </Stack>
                    ) : null}
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            color: tokens.ColorIconSubtle,
                            flexShrink: 0,
                        }}
                    >
                        <MagnifyingGlassIcon size={tokens.SizingIconM} />
                    </Box>
                </Stack>
            </Box>
        </Box>
    )

    return (
        <PdfViewerContainer
            sx={{
                position: 'relative',
                gap: floatingSearchControls ? 0 : tokens.SizingSpacing150,
                padding: floatingSearchControls ? 0 : tokens.SizingSpacing150,
            }}
        >
            {floatingSearchControls ? (
                floatingSearchControl
            ) : (
                <>
                    <HeaderBar>{searchControls}</HeaderBar>
                    {hasActiveSearch && searchMessage ? (
                        <SearchInfo variant="textS" color={tokens.ColorTextSubtle}>
                            {searchMessage}
                        </SearchInfo>
                    ) : null}
                </>
            )}
            <DocumentWrapper
                ref={attachViewerRef}
                onScroll={handleViewerScroll}
                style={{
                    width: '100%',
                    maxWidth: `${PDF_STAGE_WIDTH_PX}px`,
                    margin: '0 auto',
                    minHeight: pageHeight === null && initialDocumentMinHeightPx ? `${initialDocumentMinHeightPx}px` : undefined,
                    maxHeight: pageHeight ? `${Math.min(pageHeight, PDF_STAGE_HEIGHT_PX)}px` : `${PDF_STAGE_HEIGHT_PX}px`,
                    overflowY: 'auto',
                    overflowX: 'auto',
                }}
            >
                <Document
                    options={pdfOptions}
                    file={pdfUrl}
                    onLoadSuccess={handleDocumentLoadSuccessInternal}
                    loading={null}
                    error={<Typography variant="textM">{copy.pdfLoadError}</Typography>}
                >
                    {Array.from({ length: loadedDocumentPages }, (_, index) => (
                        <div
                            key={`page_${index + 1}`}
                            ref={(element) => {
                                pageRefs.current[index] = element
                            }}
                            style={{
                                width: pageWidthPx ? `${pageWidthPx}px` : '100%',
                                position: 'relative',
                                margin: zoom < 1 ? '0 auto' : 0,
                                boxSizing: 'border-box',
                                border: showPageShadow ? `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}` : undefined,
                                boxShadow: showPageShadow ? tokens.Popup.boxShadow : undefined,
                            }}
                        >
                            <SearchHighlightOverlay>
                                {(searchHighlights[index + 1] ?? []).map((rect, rectIndex) => (
                                    <SearchHighlightRect
                                        key={`search_highlight_${index + 1}_${rectIndex}`}
                                        style={{
                                            left: `${rect.x * 100}%`,
                                            top: `${rect.y * 100}%`,
                                            width: `${rect.width * 100}%`,
                                            height: `${rect.height * 100}%`,
                                        }}
                                    />
                                ))}
                            </SearchHighlightOverlay>
                            <FocusHighlightOverlay>
                                {(focusHighlights[index + 1] ?? []).map((rect, rectIndex) => (
                                    <FocusHighlightRect
                                        key={`focus_highlight_${index + 1}_${rectIndex}`}
                                        style={{
                                            left: `${rect.x * 100}%`,
                                            top: `${rect.y * 100}%`,
                                            width: `${rect.width * 100}%`,
                                            height: `${rect.height * 100}%`,
                                        }}
                                    />
                                ))}
                            </FocusHighlightOverlay>
                            <Page pageNumber={index + 1} renderAnnotationLayer={false} width={renderedPageWidthPx ?? undefined} />
                        </div>
                    ))}
                </Document>
            </DocumentWrapper>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    padding: 0,
                    paddingLeft: tokens.SizingSpacing300,
                    paddingRight: tokens.SizingSpacing300,
                    paddingBottom: tokens.SizingSpacing200,
                    pointerEvents: 'none',
                    zIndex: 8,
                }}
            >
                <Stack direction="row" gap={tokens.SizingSpacing100} sx={{ pointerEvents: 'auto' }}>
                    <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                        <IconButton
                            size="small"
                            variant="secondary"
                            icon={<CaretLeftIcon />}
                            ariaLabel={copy.previousPage}
                            tooltip={copy.previousPage}
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage <= 1}
                        />
                    </Box>
                    <InputBase
                        value={jumpPageInput}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setJumpPageInput(event.target.value)
                        }}
                        onBlur={() => {
                            setJumpPageInput(String(currentPage))
                        }}
                        onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                            if (event.key !== 'Enter') {
                                return
                            }

                            const parsed = parseInt(jumpPageInput, 10)
                            if (!Number.isNaN(parsed)) {
                                goToPage(parsed)
                            } else {
                                setJumpPageInput(String(currentPage))
                            }
                        }}
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                            'aria-label': copy.page,
                        }}
                        sx={{
                            width: '60px',
                            height: '32px',
                            padding: `0 ${tokens.SizingSpacing150}`,
                            borderRadius: tokens.SizingCornerS,
                            border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
                            backgroundColor: tokens.ColorSurfaceDefault,
                            pointerEvents: 'auto',
                            '& .MuiInputBase-input': {
                                textAlign: 'center',
                                ...tokens.TextM,
                                color: tokens.ColorTextBold,
                            },
                            ...tokens.Popup,
                        }}
                    />
                    <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                        <IconButton
                            size="small"
                            variant="secondary"
                            icon={<CaretRightIcon />}
                            ariaLabel={copy.nextPage}
                            tooltip={copy.nextPage}
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage >= maxAvailablePages}
                        />
                    </Box>
                </Stack>

                <Stack direction="row" gap={tokens.SizingSpacing100} sx={{ pointerEvents: 'auto' }}>
                    <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                        <IconButton
                            size="small"
                            variant="secondary"
                            icon={<MinusIcon />}
                            ariaLabel={copy.zoomOut}
                            tooltip={copy.zoomOut}
                            onClick={() => handleZoomStep('out')}
                            disabled={zoom <= ZOOM_MIN + 0.0001}
                        />
                    </Box>
                    <Box bgcolor={tokens.ColorBackgroundDark} borderRadius={tokens.SizingCornerS} sx={{ ...tokens.Popup }}>
                        <IconButton
                            size="small"
                            variant="secondary"
                            icon={<PlusIcon />}
                            ariaLabel={copy.zoomIn}
                            tooltip={copy.zoomIn}
                            onClick={() => handleZoomStep('in')}
                            disabled={zoom >= ZOOM_MAX - 0.0001}
                        />
                    </Box>
                </Stack>
            </Box>
        </PdfViewerContainer>
    )
}
